<template>
  <div class="product-detail-image-card overflow-hidden no-border flex flex-col group">
    <div class="image-container relative flex-1 min-h-0">
      <Button
        v-if="canToggle"
        class="version-badge"
        severity="secondary"
        size="small"
        @click="toggleVersion"
      >
        {{ viewMode === 'edited' ? 'Show original' : 'Show edited' }}
      </Button>
      <a
        :href="imageFullSizeUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="fullsize-link"
        title="Open full size"
      >
        <i class="pi pi-external-link" />
      </a>
      <div
        class="block aspect-square overflow-hidden rounded-lg p-2"
        :class="
          image.attention || image.hasFailedTasks
            ? 'bg-red-500/40'
            : image.hasPendingTasks
              ? 'bg-blue-500/40'
              : 'bg-surface-800'
        "
      >
        <img :src="imageThumbnailUrl" class="product-image w-full h-full object-contain" />
      </div>
    </div>
    <div class="px-2 pt-1.5 flex items-center justify-end gap-1 min-w-0">
      <Button
        @click.prevent="emit('delete')"
        icon="pi pi-trash"
        text
        rounded
        size="small"
        class="transition-opacity opacity-0 group-hover:opacity-100 text-surface-400 dark:text-surface-500"
        severity="secondary"
      />
      <Button
        @click.prevent="workflowDialogVisible = true"
        icon="pi pi-sitemap"
        text
        rounded
        size="small"
        class="transition-opacity"
        :class="{
          'text-red-500': image.hasFailedTasks,
          'text-blue-500':
            !image.hasFailedTasks && image.hasPendingTasks,
          'text-surface-400 dark:text-surface-500 opacity-0 group-hover:opacity-100':
            !image.hasFailedTasks && !image.hasPendingTasks,
        }"
        :severity="
          image.hasFailedTasks
            ? 'danger'
            : image.hasPendingTasks
              ? 'info'
              : 'secondary'
        "
      />
      <Button
        @click.prevent="emit('toggleAttention')"
        icon="pi pi-exclamation-triangle"
        text
        rounded
        size="small"
        class="transition-opacity"
        :class="{
          'text-red-500': image.attention,
          'text-surface-400 dark:text-surface-500 opacity-0 group-hover:opacity-100': !image.attention,
        }"
        :severity="image.attention ? 'danger' : 'secondary'"
      />
    </div>
    <ImageWorkflowDialog v-model:visible="workflowDialogVisible" :image-id="image.id" />
    <div
      v-if="image.attentionText"
      class="footer flex items-center gap-2 px-2 pb-2 flex-wrap"
    >
      <div
        class="flex-1 text-yellow-400 text-xs min-w-0 attention-text"
        style="word-break: break-word; white-space: pre-line"
      >
        {{ image.attentionText }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.image-container {
  position: relative;
}

.version-badge {
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 1;
  background: rgba(0, 0, 0, 0.5) !important;
  color: white !important;
  padding: 8px 8px !important;
  border-radius: 4px !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px;
  border: none !important;
  cursor: pointer;
  line-height: 1;
}

.fullsize-link {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 6px;
  color: white;
  text-decoration: none;
  transition: background 0.2s;
}

.fullsize-link:hover {
  background: rgba(0, 0, 0, 0.85);
}

.product-image {
  display: block;
}

.attention-text {
  max-width: 240px;
}
</style>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { ImageDto, ImageVersionDto } from '@/api/generated';
import Button from 'primevue/button';
import ImageWorkflowDialog from '@/modules/products/workflow/ImageWorkflowDialog.vue';

const props = defineProps<{
  productId: string;
  image: ImageDto;
}>();

const emit = defineEmits<{
  delete: [];
  toggleAttention: [];
}>();

const workflowDialogVisible = ref(false);

type ViewMode = 'original' | 'edited';
const viewMode = ref<ViewMode>('edited');

const _selectedImages = defineModel('selectedImages');

const originalVersion = computed(() => props.image.versions?.find(v => v.original));

const editedVersion = computed(
  () =>
    props.image.versions?.find(v => v.id === props.image.selectedVersionId) ??
    originalVersion.value,
);

const currentVersion = computed(() =>
  viewMode.value === 'original' ? originalVersion.value : editedVersion.value,
);

const canToggle = computed(
  () =>
    originalVersion.value &&
    editedVersion.value &&
    originalVersion.value.id !== editedVersion.value.id,
);

function toggleVersion() {
  viewMode.value = viewMode.value === 'original' ? 'edited' : 'original';
}

const imageThumbnailUrl = computed(() => {
  return getImageUrl(currentVersion.value, { width: 400 });
});

const imageFullSizeUrl = computed(() => {
  return getImageUrl(currentVersion.value, { fullSize: true });
});

function getImageUrl(
  version: ImageVersionDto | undefined,
  opts: {
    fullSize?: boolean;
    width?: number;
  },
): string | undefined {
  const url = version?.url;

  if (!url) {
    return undefined;
  }

  let imageOpts = '';
  if (opts.fullSize) {
    imageOpts += 's0';
  } else if (opts.width) {
    const width = devicePixelRatio * opts.width;
    imageOpts += `s${width}`;
  }

  return imageOpts.length ? `${url}=${imageOpts}` : url;
}
</script>
